import { ChevronLeft, ChevronRight } from "@mui/icons-material";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import { useEffect, useRef, useState } from "react";

const THUMB_SIZE = 80;
const GAP = 10; // px gap between thumbnails (kept tight to fit 5 on standard laptops)
const MAX_VISIBLE_THUMBNAILS = 5;

interface ThumbnailCarouselProps {
  thumbnails: string[];
  productName: string;
  activeImage: string | null;
  onSelect: (src: string) => void;
  onOffsetChange?: (offset: number) => void;
  onVisibleCountChange?: (visibleCount: number) => void;
}

export function ThumbnailCarousel({
  thumbnails,
  productName,
  activeImage,
  onSelect,
  onOffsetChange,
  onVisibleCountChange,
}: ThumbnailCarouselProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [offset, setOffset] = useState(0);
  const [visibleCount, setVisibleCount] = useState(5);

  // Report offset and visible count changes so the dots indicator can stay in sync
  const onOffsetChangeRef = useRef(onOffsetChange);
  onOffsetChangeRef.current = onOffsetChange;
  useEffect(() => {
    onOffsetChangeRef.current?.(offset);
  }, [offset]);

  const onVisibleCountChangeRef = useRef(onVisibleCountChange);
  onVisibleCountChangeRef.current = onVisibleCountChange;
  useEffect(() => {
    onVisibleCountChangeRef.current?.(visibleCount);
  }, [visibleCount]);

  // Measure how many thumbnails actually fit in the container
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const computeVisible = (width: number) => {
      // Each item occupies THUMB_SIZE + GAP, but the last item has no trailing gap
      // So: w = visibleCount * THUMB_SIZE + (visibleCount - 1) * GAP
      // => visibleCount = floor((w + GAP) / (THUMB_SIZE + GAP))
      const count = Math.max(1, Math.floor((width + GAP) / (THUMB_SIZE + GAP)));
      setVisibleCount(Math.min(MAX_VISIBLE_THUMBNAILS, thumbnails.length, count));
    };

    // Initial measurement (float precision)
    computeVisible(el.getBoundingClientRect().width);

    const ro = new ResizeObserver((entries) => {
      for (const entry of entries) {
        computeVisible(entry.contentRect.width);
      }
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [thumbnails.length]);

  // Clamp offset whenever the strip length or visible window changes
  useEffect(() => {
    setOffset((prev) => {
      const maxOffset = Math.max(0, thumbnails.length - visibleCount);
      return Math.min(prev, maxOffset);
    });
  }, [thumbnails.length, visibleCount]);

  const currentIndex = activeImage ? thumbnails.indexOf(activeImage) : 0;
  const canPrev = currentIndex > 0;
  const canNext = currentIndex < thumbnails.length - 1;

  const handlePrev = () => {
    const newIndex = currentIndex - 1;
    if (newIndex >= 0) {
      const newImage = thumbnails[newIndex];
      if (!newImage) return;
      onSelect(newImage);
      setOffset((prevOffset) => (newIndex < prevOffset ? newIndex : prevOffset));
    }
  };

  const handleNext = () => {
    const newIndex = currentIndex + 1;
    if (newIndex < thumbnails.length) {
      const newImage = thumbnails[newIndex];
      if (!newImage) return;
      onSelect(newImage);
      setOffset((prevOffset) => {
        if (newIndex >= prevOffset + visibleCount) {
          return newIndex - visibleCount + 1;
        }
        return prevOffset;
      });
    }
  };

  // Keep active thumbnail in view whenever the selected image changes
  useEffect(() => {
    setOffset((prevOffset) => {
      if (currentIndex < prevOffset) {
        return currentIndex;
      }
      if (currentIndex >= prevOffset + visibleCount) {
        return currentIndex - visibleCount + 1;
      }
      return prevOffset;
    });
  }, [currentIndex, visibleCount]);

  // ── Touch / drag gesture support ──
  const touchStartX = useRef<number | null>(null);
  const touchStartY = useRef<number | null>(null);
  const SWIPE_THRESHOLD = 40;

  const onTouchStart = (e: React.TouchEvent<HTMLDivElement>) => {
    const touch = e.touches[0];
    if (!touch) return;

    touchStartX.current = touch.clientX;
    touchStartY.current = touch.clientY;
  };

  const onTouchMove = (e: React.TouchEvent<HTMLDivElement>) => {
    if (touchStartX.current === null || touchStartY.current === null) return;

    const touch = e.touches[0];
    if (!touch) return;

    const dx = Math.abs(touch.clientX - touchStartX.current);
    const dy = Math.abs(touch.clientY - touchStartY.current);

    // If vertical scroll dominates, cancel the swipe intent
    if (dy > dx && dy > 10) {
      touchStartX.current = null;
      touchStartY.current = null;
    }
  };

  const onTouchEnd = (e: React.TouchEvent<HTMLDivElement>) => {
    if (touchStartX.current === null) return;

    const touch = e.changedTouches[0];
    if (!touch) return;

    const dx = touch.clientX - touchStartX.current;

    if (dx < -SWIPE_THRESHOLD) {
      // Swipe left → reveal later thumbnails
      setOffset((prev) => {
        const maxOffset = Math.max(0, thumbnails.length - visibleCount);
        return Math.min(maxOffset, prev + 1);
      });
    } else if (dx > SWIPE_THRESHOLD) {
      // Swipe right → reveal earlier thumbnails
      setOffset((prev) => Math.max(0, prev - 1));
    }

    touchStartX.current = null;
    touchStartY.current = null;
  };

  return (
    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 1 }}>
      <IconButton
        size="small"
        onClick={handlePrev}
        disabled={!canPrev}
        aria-label="Previous images"
        sx={{
          flexShrink: 0,
          color: "var(--color-graphite)",
          bgcolor: "transparent",
          border: "1px solid var(--color-smoke)",
          borderRadius: "4px",
          width: "32px",
          height: "32px",
          p: 0.5,
          "&:hover": { bgcolor: "var(--color-light-dust)" },
          "&.Mui-disabled": {
            color: "#D9D9D9",
            bgcolor: "transparent",
          },
        }}
      >
        <ChevronLeft />
      </IconButton>
      <Box
        ref={containerRef}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        sx={{
          display: "flex",
          gap: "10px",
          overflow: "hidden",
          touchAction: "pan-y",
          flex: 1,
          minWidth: 0,
          justifyContent: "center",
        }}
      >
        {thumbnails.slice(offset, offset + visibleCount).map((src, i) => {
          const idx = offset + i;
          const isActive = activeImage === src || (!activeImage && idx === 0);
          return (
            <Box
              key={src}
              component="button"
              onClick={() => onSelect(src)}
              aria-label={`View image ${idx + 1}`}
              aria-pressed={isActive}
              sx={{
                width: THUMB_SIZE,
                height: THUMB_SIZE,
                flexShrink: 0,
                borderRadius: "8px",
                overflow: "hidden",
                border: isActive
                  ? "2px solid var(--color-dark-dust)"
                  : "1px solid var(--color-smoke)",
                cursor: "pointer",
                p: 0,
                bgcolor: "white",
                boxShadow: isActive ? "0px 4px 10px rgba(0,0,0,0.12)" : "none",
                transition: "border-color 0.15s",
                "&:focus-visible": {
                  outline: "2px solid var(--color-dust-ink)",
                  outlineOffset: "2px",
                },
              }}
            >
              <Box
                component="img"
                src={src}
                alt={`${productName} — view ${idx + 1}`}
                onError={(e) => {
                  (e.target as HTMLImageElement).src = "https://placehold.co/200x200?text=No+image";
                }}
                sx={{ width: "100%", height: "100%", objectFit: "cover" }}
              />
            </Box>
          );
        })}
      </Box>

      <IconButton
        size="small"
        onClick={handleNext}
        disabled={!canNext}
        aria-label="Next images"
        sx={{
          flexShrink: 0,
          color: "var(--color-graphite)",
          bgcolor: "transparent",
          border: "1px solid var(--color-smoke)",
          borderRadius: "4px",
          width: "32px",
          height: "32px",
          "&:hover": { bgcolor: "var(--color-light-dust)" },
          "&.Mui-disabled": {
            color: "var(--color-smoke)",
            bgcolor: "transparent",
          },
        }}
      >
        <ChevronRight />
      </IconButton>
    </Box>
  );
}

// ─── Dots indicator ──────────────────────────────────────────────────────────────

const MAX_DOTS = 5;

export interface DotsIndicatorProps {
  count: number;
  activeIndex: number;
  offset?: number;
  visibleCount?: number;
}

export function DotsIndicator({ count, activeIndex, offset, visibleCount }: DotsIndicatorProps) {
  const ACTIVE_DOT_WIDTH = 10;
  const INACTIVE_DOT_WIDTH = 6;
  const DOT_HEIGHT = 6;
  const DOT_GAP = 6;
  const DOT_Y_PADDING = 2;
  const DOT_ROW_HEIGHT = DOT_HEIGHT + DOT_Y_PADDING * 2;

  const visibleWindowSize = Math.min(count, visibleCount ?? MAX_DOTS);

  // Small datasets: direct 1-to-1 mapping, every dot visible
  if (count <= visibleWindowSize) {
    const dots: React.ReactNode[] = [];
    for (let i = 0; i < count; i++) {
      const isActive = i === activeIndex;
      dots.push(
        <Box
          key={`small-dot-${count}-${i}`}
          sx={{
            width: isActive ? ACTIVE_DOT_WIDTH : INACTIVE_DOT_WIDTH,
            height: DOT_HEIGHT,
            borderRadius: "4px",
            bgcolor: isActive ? "var(--color-dark-dust)" : "var(--color-smoke)",
            transition: "width 0.25s ease, background-color 0.2s ease",
            flexShrink: 0,
          }}
        />,
      );
    }
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          gap: `${DOT_GAP}px`,
          mt: 1,
          minHeight: DOT_ROW_HEIGHT,
          py: `${DOT_Y_PADDING}px`,
          overflow: "visible",
          lineHeight: 0,
        }}
      >
        {dots}
      </Box>
    );
  }

  // Large datasets: sliding rail that moves in sync with thumbnails.
  // Dots sit in fixed-width slots so the transform distance is always predictable.
  const windowStart =
    offset !== undefined
      ? Math.min(Math.max(0, offset), Math.max(0, count - visibleWindowSize))
      : Math.max(0, Math.min(activeIndex - (visibleWindowSize - 1), count - visibleWindowSize));

  const railSlide = -(windowStart * (INACTIVE_DOT_WIDTH + DOT_GAP));
  const visibleWindowWidth =
    ACTIVE_DOT_WIDTH +
    (visibleWindowSize - 1) * INACTIVE_DOT_WIDTH +
    (visibleWindowSize - 1) * DOT_GAP;

  // Build all dots using the real visual widths so the sliding state looks
  // identical to the direct-mapped state, with only the rail motion changing.
  const dotSlots: React.ReactNode[] = [];
  for (let i = 0; i < count; i++) {
    const isActive = i === activeIndex;
    dotSlots.push(
      <Box
        key={`dot-slot-${i}`}
        sx={{
          width: isActive ? ACTIVE_DOT_WIDTH : INACTIVE_DOT_WIDTH,
          height: DOT_HEIGHT,
          borderRadius: "4px",
          bgcolor: isActive ? "var(--color-dark-dust)" : "var(--color-smoke)",
          flexShrink: 0,
          transition: "width 0.25s ease, background-color 0.2s ease",
        }}
      />,
    );
  }

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        gap: `${DOT_GAP}px`,
        mt: 1,
        minHeight: DOT_ROW_HEIGHT,
        py: `${DOT_Y_PADDING}px`,
        overflow: "visible",
        lineHeight: 0,
      }}
    >
      {/* Left peek indicator */}
      <Box
        sx={{
          width: 4,
          height: 4,
          borderRadius: "50%",
          bgcolor: "var(--color-smoke)",
          flexShrink: 0,
          alignSelf: "center",
          opacity: windowStart > 0 ? 1 : 0,
          transition: "opacity 0.25s ease",
        }}
      />

      {/* Sliding window */}
      <Box
        sx={{
          overflowX: "hidden",
          overflowY: "visible",
          width: visibleWindowWidth,
          minHeight: DOT_ROW_HEIGHT,
          flexShrink: 0,
          display: "flex",
          alignItems: "center",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: `${DOT_GAP}px`,
            transform: `translateX(${railSlide}px)`,
            transition: "transform 0.35s cubic-bezier(0.22, 1, 0.36, 1)",
            willChange: "transform",
            minHeight: DOT_ROW_HEIGHT,
          }}
        >
          {dotSlots}
        </Box>
      </Box>

      {/* Right peek indicator */}
      <Box
        sx={{
          width: 4,
          height: 4,
          borderRadius: "50%",
          bgcolor: "var(--color-smoke)",
          flexShrink: 0,
          alignSelf: "center",
          opacity: windowStart + visibleWindowSize < count ? 1 : 0,
          transition: "opacity 0.25s ease",
        }}
      />
    </Box>
  );
}
